import styled from "@emotion/styled";
import { useState } from "react";
import { LobbyGameParams } from "~/business/LobbyGameParams";
import { envVariables } from "~/infra";
import { Spacing } from "~/jsx/theme";
import { NewLobbyGame, isLocalhost, motion } from "~/utils";
import { XButton } from "../design";

const Container = styled.div`
  border: solid 1px white;
  border-radius: 8px;
  width: fit-content;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: flex-end;

  & label {
    margin-right: 12px;
    text-align: right;
  }
  & input {
    width: 40px;
  }
`;

export const XLobbyNewGameForm = ({
  onCreate,
  visible,
}: {
  onCreate: (lobbyGame: NewLobbyGame) => void;
  visible: boolean;
}) => {
  const defaultParams: LobbyGameParams = {
    useBaseGame: true,
    useIxExtension: false,
  };
  const [lobbyGame, setLobbyGame] = useState<NewLobbyGame>({
    id: Math.random().toString().substring(2),
    settings: {
      minPlayerCount: 2,
      maxPlayerCount: 4,
      params: LobbyGameParams.toRecord(defaultParams),
    },
  });
  const {
    settings: { maxPlayerCount, minPlayerCount, params = {} },
  } = lobbyGame;
  const parsedParams = LobbyGameParams.fromRecord(params);
  const { useIxExtension, useBaseGame } = parsedParams;

  return (
    <motion.div
      layoutId="new-game-form"
      style={{
        ...(visible
          ? { width: "fit-content", height: "fit-content" }
          : { width: 0, height: 0, maxWidth: 0, maxHeight: 0 }),
        overflow: "hidden",
      }}
    >
      <Container>
        <div>
          <label>Minimum number of players</label>
          <input
            type="number"
            min={2}
            max={4}
            value={minPlayerCount}
            onChange={(event) => {
              const newValue = parseInt(event.target.value, 10);
              const newMax = Math.max(newValue, maxPlayerCount);
              setLobbyGame({
                ...lobbyGame,
                settings: {
                  ...lobbyGame.settings,
                  minPlayerCount: newValue,
                  maxPlayerCount: newMax,
                },
              });
            }}
          />
        </div>
        <div>
          <label>Maximum number of players</label>
          <input
            type="number"
            min={2}
            max={4}
            value={maxPlayerCount}
            onChange={(event) => {
              const newValue = parseInt(event.target.value, 10);
              const newMin = Math.min(newValue, minPlayerCount);
              setLobbyGame({
                ...lobbyGame,
                settings: {
                  ...lobbyGame.settings,
                  minPlayerCount: newMin,
                  maxPlayerCount: newValue,
                },
              });
            }}
          />
        </div>
        {isLocalhost() && (
          <div>
            <label>Base game</label>
            <input
              type="checkbox"
              checked={useBaseGame}
              onChange={() =>
                setLobbyGame({
                  ...lobbyGame,
                  settings: {
                    ...lobbyGame.settings,
                    params: LobbyGameParams.toRecord({
                      ...parsedParams,
                      useBaseGame: !useBaseGame,
                    }),
                  },
                })
              }
            />
          </div>
        )}
        <div>
          <label>Ix extension</label>
          <input
            disabled={!envVariables.enableIxExtension}
            type="checkbox"
            checked={useIxExtension}
            onChange={() =>
              setLobbyGame({
                ...lobbyGame,
                settings: {
                  ...lobbyGame.settings,
                  params: LobbyGameParams.toRecord({
                    ...parsedParams,
                    useIxExtension: !useIxExtension,
                  }),
                },
              })
            }
          />
        </div>
        <XButton
          style={{
            width: 200,
            textAlign: "center",
            margin: "auto",
            marginTop: Spacing.px12,
          }}
          variant="action"
          onClick={() => {
            onCreate(lobbyGame);
          }}
        >
          Create game
        </XButton>
      </Container>
    </motion.div>
  );
};
